import { Alert } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH,
    PLATFORM_SUPPORT_MESSAGE_MIN_LENGTH,
    PLATFORM_SUPPORT_REASON_IDS,
    PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT,
    type PlatformSupportReasonId,
} from "../../../functions/src/api/constants";
import { SCHOOL_DATA_PATH } from "../../../functions/src/admin/paths";
import { currentSchoolKey, functions } from "../../api/firebase";
import { useDatabaseVal } from "../../api/firebaseHooks";
import { langContext, termsUrl } from "../../api/Lang";
import { uppercaseForLang } from "../../api/langConfig";
import { invokeCallableWithRetry } from "../../api/callableRetry";
import { captureException } from "../../api/sentry";
import type { SchoolData, UserProfile } from "../../types";
import { usePlatformSupportStrings } from "../platform-support-strings";
import { usePlatformSupportSnapshots } from "./usePlatformSupportSnapshots";
import type { PlatformSupportLeaveGuard } from "./platformSupportLeaveGuard";

type PlatformSupportProps = {
    profile: UserProfile | null | undefined;
    leaveGuardRef: React.MutableRefObject<PlatformSupportLeaveGuard | null>;
};

export default function PlatformSupport({ profile, leaveGuardRef }: PlatformSupportProps) {
    const strings = usePlatformSupportStrings();
    const [lang] = useContext(langContext);
    const [schoolData] = useDatabaseVal<SchoolData>(SCHOOL_DATA_PATH);
    const [reason, setReason] = useState<PlatformSupportReasonId | "">("");
    const [message, setMessage] = useState("");
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const [snapshotPickError, setSnapshotPickError] = useState<string | null>(null);
    const {
        getUploadSessionId,
        slots: snapshotSlots,
        readySlots,
        uploading: snapshotUploading,
        getSnapshotForSlot,
        addSnapshotToSlot,
        removeSnapshot,
        resetSnapshots,
        abandonSnapshots,
    } = usePlatformSupportSnapshots({
        invalidTypeMessage: strings.snapshotsInvalidType,
        tooLargeMessage: strings.snapshotsTooLarge,
        uploadFailedMessage: strings.snapshotsUploadFailed,
    });

    const displayEmail = profile?.email?.trim() || "";
    const snapshotBusy = snapshotSlots.some((s) => s.status === "uploading");
    const reasonSelected = !!reason;
    const formDisabled = submitting;
    const canSubmit =
        reasonSelected && message.trim().length >= PLATFORM_SUPPORT_MESSAGE_MIN_LENGTH;
    const hasDraftContent = useMemo(
        () =>
            !success
            && (
                message.trim().length > 0
                || snapshotSlots.some((s) => s.status === "ready" || s.status === "uploading")
            ),
        [success, message, snapshotSlots]
    );
    const hasDraftContentRef = useRef(hasDraftContent);
    hasDraftContentRef.current = hasDraftContent;
    const sendAnotherButtonRef = useRef<HTMLButtonElement>(null);
    const messageFieldRef = useRef<HTMLTextAreaElement>(null);
    const prevReasonRef = useRef<PlatformSupportReasonId | "">("");

    useEffect(() => {
        if (success) {
            sendAnotherButtonRef.current?.focus();
        }
    }, [success]);

    useEffect(() => {
        if (reason && !prevReasonRef.current) {
            messageFieldRef.current?.focus();
        }
        prevReasonRef.current = reason;
    }, [reason]);

    useEffect(() => {
        const onBeforeUnload = (event: BeforeUnloadEvent): void => {
            if (!hasDraftContentRef.current) {
                return;
            }
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    useEffect(() => {
        leaveGuardRef.current = {
            hasDraftContent: () => hasDraftContentRef.current,
        };
        return () => {
            leaveGuardRef.current = null;
            if (hasDraftContentRef.current) {
                void abandonSnapshots();
            }
        };
    }, [abandonSnapshots, leaveGuardRef]);

    const validateMessage = (): string | null => {
        const trimmed = message.trim();
        if (!trimmed) {
            return strings.messageRequired;
        }
        if (trimmed.length < PLATFORM_SUPPORT_MESSAGE_MIN_LENGTH) {
            return strings.messageTooShort;
        }
        if (trimmed.length > PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH) {
            return strings.messageTooLong;
        }
        return null;
    };

    const onSubmit = async (): Promise<void> => {
        if (!reason) {
            return;
        }
        const validationError = validateMessage();
        if (validationError) {
            setFieldError(validationError);
            return;
        }
        setFieldError(null);
        setSubmitFailed(false);
        setSubmitting(true);
        try {
            const submitOrderPlatformSupport = functions.httpsCallable("submitOrderPlatformSupport");
            await invokeCallableWithRetry(() =>
                submitOrderPlatformSupport({
                    message: message.trim(),
                    reason,
                    schoolKey: currentSchoolKey,
                    ...(readySlots.length
                        ? { uploadSessionId: getUploadSessionId(), snapshotSlots: readySlots }
                        : {}),
                })
            );
            setSuccess(true);
            setReason("");
            setMessage("");
            resetSnapshots();
            setSnapshotPickError(null);
        } catch (error) {
            captureException(error);
            setSubmitFailed(true);
        } finally {
            setSubmitting(false);
        }
    };

    const mealContactBlock = useMemo(() => {
        const email = schoolData?.canteenEmail?.trim();
        const tel = schoolData?.canteenTel?.trim();
        if (!email && !tel) {
            return <p className="platform-support-meal-card__body">{strings.mealServiceContactMissing}</p>;
        }
        return (
            <div className="platform-support-meal-card__contacts">
                {email && (
                    <p className="platform-support-meal-card__contact-line">
                        {strings.mealServiceEmailPrefix}
                        <a href={`mailto:${email}`}>{email}</a>
                    </p>
                )}
                {tel && (
                    <p className="platform-support-meal-card__contact-line">
                        {strings.mealServicePhonePrefix}
                        <a href={`tel:${tel}`}>{tel}</a>
                    </p>
                )}
            </div>
        );
    }, [schoolData?.canteenEmail, schoolData?.canteenTel, strings]);

    if (success) {
        return (
            <div className="base_margin_setup platform-support-page">
                <div className="platform-support-success" role="status" aria-live="polite">
                    <h1 className="order-heading platform-support-page__title">{strings.successTitle}</h1>
                    <p className="order-remark platform-support-page__intro">{strings.successBody}</p>
                    <div className="platform-support-actions platform-support-actions--compact">
                        <button
                            ref={sendAnotherButtonRef}
                            type="button"
                            className="hollow-button platform-support-send-another"
                            onClick={() => setSuccess(false)}
                        >
                            {strings.sendAnother}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="base_margin_setup platform-support-page">
            <h1 className="order-heading platform-support-page__title">{strings.pageHeading}</h1>

            <section className="platform-support-meal-card" aria-labelledby="platform-support-meal-heading">
                <h2 id="platform-support-meal-heading" className="platform-support-meal-card__title">
                    {uppercaseForLang(strings.mealServiceTitle, lang)}
                </h2>
                <p className="platform-support-meal-card__body">{strings.mealServiceBody}</p>
                {mealContactBlock}
            </section>

            <section
                className={`rectangle-gray platform-support-form-section${reasonSelected ? "" : " platform-support-form-section--collapsed"}`}
                aria-labelledby="platform-support-form-heading"
            >
                <div
                    className={`platform-support-intro-accordion${reasonSelected ? " platform-support-intro-accordion--expanded" : " platform-support-intro-accordion--collapsed"}`}
                >
                    <h2 id="platform-support-form-heading" className="header-child platform-support-form-section__title">
                        {strings.platformSectionTitle}
                    </h2>
                    <p className="platform-support-form-section__intro platform-support-intro-accordion__intro">
                        {strings.platformSectionBody}
                    </p>
                    <div className="platform-support-form platform-support-form--reason-step">
                        <div className={`platform-support-field${formDisabled ? " platform-support-field--locked" : ""}`}>
                            <label className="platform-support-field__label" htmlFor="platform-support-reason">
                                {strings.reasonLabel}
                            </label>
                            <select
                                id="platform-support-reason"
                                className={`platform-support-field__select${reason ? "" : " platform-support-field__select--placeholder"}`}
                                value={reason}
                                disabled={formDisabled}
                                aria-controls={reasonSelected ? "platform-support-form-panel" : undefined}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setReason(next ? (next as PlatformSupportReasonId) : "");
                                }}
                            >
                                <option value="">{strings.reasonPlaceholder}</option>
                                {PLATFORM_SUPPORT_REASON_IDS.map((id) => (
                                    <option key={id} value={id}>
                                        {strings.reasonOptions[id]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {reasonSelected && (
                        <div
                            id="platform-support-form-panel"
                            className="platform-support-intro-accordion__panel"
                            role="region"
                            aria-label={strings.formPanelRegionLabel}
                            aria-live="polite"
                            aria-busy={formDisabled || snapshotUploading || snapshotBusy}
                        >
                            <p className="platform-support-form-panel__status" role="status">
                                {strings.formPanelExpandedAnnouncement}
                            </p>
                            {submitFailed && (
                                <Alert severity="error" className="platform-support-form-section__alert">
                                    {strings.submitFailed}
                                </Alert>
                            )}

                            <div className="platform-support-form">
                    <div className={`platform-support-field${formDisabled ? " platform-support-field--locked" : ""}`}>
                        <label className="platform-support-field__label" htmlFor="platform-support-message">
                            {strings.messageLabel}
                        </label>
                        <div className="order-remarks-box platform-support-field__textarea-wrap">
                            <textarea
                                ref={messageFieldRef}
                                id="platform-support-message"
                                aria-invalid={!!fieldError}
                                aria-describedby="platform-support-message-hint"
                                disabled={!reasonSelected || formDisabled}
                                placeholder={
                                    reasonSelected
                                        ? strings.messagePlaceholders[reason]
                                        : strings.messagePlaceholderDefault
                                }
                                value={message}
                                rows={5}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    if (next.length <= PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH) {
                                        setMessage(next);
                                        if (fieldError) {
                                            setFieldError(null);
                                        }
                                    }
                                }}
                            />
                        </div>
                        <p
                            id="platform-support-message-hint"
                            className={`platform-support-field__hint${fieldError ? " platform-support-field__hint--error" : ""}`}
                        >
                            {fieldError
                                || strings.messageCharCount(
                                    message.trim().length,
                                    PLATFORM_SUPPORT_MESSAGE_MIN_LENGTH,
                                    PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH
                                )}
                        </p>
                    </div>

                    <div className={`platform-support-field${!reasonSelected || formDisabled ? " platform-support-field--locked" : ""}`}>
                        <div className="platform-support-field__label-row">
                            <span className="platform-support-field__label" id="platform-support-snapshots-label">
                                {strings.snapshotsLabel}
                            </span>
                            <span className="platform-support-field__hint platform-support-snapshots-hint">
                                {strings.snapshotsHint}
                            </span>
                        </div>
                        <div
                            className="platform-support-snapshots"
                            role="group"
                            aria-labelledby="platform-support-snapshots-label"
                            aria-describedby={snapshotPickError ? "platform-support-snapshots-pick-error" : undefined}
                        >
                            {Array.from({ length: PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT }, (_, slotIndex) => {
                                const item = getSnapshotForSlot(slotIndex);
                                const slotBusy = item?.status === "uploading";
                                const slotDisabled = !reasonSelected || formDisabled || snapshotUploading || slotBusy;

                                if (item) {
                                    return (
                                        <div key={slotIndex} className="platform-support-snapshots__item">
                                            <div className="platform-support-snapshots__filled">
                                                <img
                                                    src={item.previewUrl}
                                                    alt={strings.snapshotPreviewAlt(slotIndex + 1)}
                                                    className="platform-support-snapshots__thumb"
                                                />
                                                <button
                                                    type="button"
                                                    className="platform-support-snapshots__remove-icon"
                                                    aria-label={strings.snapshotsRemoveFor(slotIndex + 1)}
                                                    disabled={slotBusy || formDisabled}
                                                    onClick={() => void removeSnapshot(item.slot)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            {item.status === "uploading" && (
                                                <span className="platform-support-snapshots__status">{strings.snapshotsUploading}</span>
                                            )}
                                            {item.status === "error" && item.errorMessage && (
                                                <span className="platform-support-snapshots__status platform-support-snapshots__status--error">
                                                    {item.errorMessage}
                                                </span>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={slotIndex} className="platform-support-snapshots__item">
                                        <label
                                            className={`platform-support-snapshots__placeholder${slotDisabled ? " platform-support-snapshots__placeholder--disabled" : ""}`}
                                        >
                                            <span className="platform-support-snapshots__placeholder-plus" aria-hidden="true">+</span>
                                            <span className="platform-support-snapshots__placeholder-label">{strings.snapshotsAdd}</span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                className="platform-support-snapshots__file-input"
                                                disabled={slotDisabled}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    e.target.value = "";
                                                    if (file) {
                                                        setSnapshotPickError(null);
                                                        void addSnapshotToSlot(file, slotIndex).catch((err) => {
                                                            if (err instanceof Error) {
                                                                setSnapshotPickError(err.message);
                                                            }
                                                        });
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        {snapshotPickError && (
                            <p
                                id="platform-support-snapshots-pick-error"
                                className="platform-support-field__hint platform-support-field__hint--error"
                            >
                                {snapshotPickError}
                            </p>
                        )}
                    </div>

                    {displayEmail && (
                        <p className="platform-support-reply-email">
                            {strings.yourEmailLabel} <strong>{displayEmail}</strong>
                        </p>
                    )}

                    <div className="platform-support-actions">
                        <button
                            type="button"
                            className={`credit-rectangle-filled platform-support-submit${submitting ? " disabled" : ""}`}
                            disabled={!canSubmit || submitting || snapshotBusy || snapshotUploading}
                            onClick={() => void onSubmit()}
                        >
                            {submitting ? strings.submitting : strings.submit}
                        </button>
                    </div>

                                <p className="platform-support-privacy">
                                    {strings.privacyPrefix}
                                    <a href={termsUrl("privacy", lang)} target="_blank" rel="noopener noreferrer">
                                        {strings.privacyLinkText}
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
